<script lang="ts">
  import { page } from '@inertiajs/svelte'
  import { inertia } from '../utils/inertia'
  import { faLanguage } from '@fortawesome/free-solid-svg-icons'
  import { matchesAlias } from '../utils/alias'
  import { getI18n } from '../i18n'
  import type { NavigationLink } from '../types'
  import Icon from './Icon.svelte'
  import LanguageButtonLinks from './LanguageButtonLinks.svelte'

  let { links }: { links: NavigationLink[] } = $props()

  const i18n = getI18n()
  let languageMenu: HTMLElement

  // The layout stays mounted across Inertia visits, so close the menu whenever the URL changes (e.g. after switching the language).
  $effect(() => {
    void page.url
    languageMenu.hidePopover()
  })
</script>

<nav class="navigation">
  <ul class="navigation-list">
    {#each links as { label, href, alias, icon, variant } (href)}
      <li class="navigation-item">
        <a
          {href}
          {@attach inertia({ href })}
          class={['navigation-link', {
            '-active': matchesAlias(page.url, alias),
            '-variant-neutral': variant === 'neutral',
            '-variant-brand': variant === 'brand',
            '-variant-warning': variant === 'warning',
            '-variant-success': variant === 'success',
          }]}
          aria-current={matchesAlias(page.url, alias) ? 'page' : undefined}
        >
          <Icon {icon} />
          {label}
        </a>
      </li>
    {/each}
    <li class="navigation-item -end">
      <button popovertarget="navigation-language-menu" class="navigation-link navigation-language-trigger">
        <Icon icon={faLanguage} />
        {i18n.t('navigation.language')}
      </button>
      <div id="navigation-language-menu" popover class="navigation-language-menu" bind:this={languageMenu}>
        <LanguageButtonLinks />
      </div>
    </li>
  </ul>
</nav>

<style>
  .navigation {
    display: none;
    align-items: stretch;
    justify-content: space-between;
    height: var(--nav-height);
    width: 100%;
    padding: var(--space-3) 0;
    margin-bottom: var(--space-12);

    @media (--tablet) {
      display: flex;
    }
  }

  .navigation-list {
    display: flex;
    flex-direction: row;
    gap: var(--space-1);
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;
    width: 100%;
  }

  .navigation-item {
    display: flex;
    align-items: center;

    &.-end {
      margin-inline-start: auto;
    }
  }

  .navigation-link {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    text-decoration: none;
    white-space: nowrap;
    background: none;
    border: none;
    cursor: pointer;
    color: var(--color-text-muted);
    background-color: transparent;
    font-size: var(--font-size-5);
    font-weight: var(--font-weight-1);
    padding: var(--space-4) var(--space-7);
    border-radius: var(--radius-6);
    transition: color var(--transition-fast), background-color var(--transition-fast);
  }

  .navigation-link.-variant-neutral:hover,
  .navigation-link.-variant-neutral.-active {
    color: var(--color-variant-neutral);
    background-color: var(--color-variant-neutral-muted);
  }

  .navigation-link.-variant-brand:hover,
  .navigation-link.-variant-brand.-active {
    color: var(--color-variant-brand);
    background-color: var(--color-variant-brand-muted);
  }

  .navigation-link.-variant-warning:hover,
  .navigation-link.-variant-warning.-active {
    color: var(--color-variant-warning);
    background-color: var(--color-variant-warning-muted);
  }

  .navigation-link.-variant-success:hover,
  .navigation-link.-variant-success.-active {
    color: var(--color-variant-success);
    background-color: var(--color-variant-success-muted);
  }

  .navigation-language-trigger {
    anchor-name: --navigation-language-anchor;

    &:hover {
      color: var(--color-text-muted);
      background-color: transparent;
    }
  }

  .navigation-item:has(> .navigation-language-menu:popover-open) .navigation-language-trigger {
    color: var(--color-variant-danger);
    background-color: var(--color-variant-danger-muted);
  }

  .navigation-language-menu {
    position: fixed;
    position-anchor: --navigation-language-anchor;
    position-area: block-end span-inline-start;
    margin: var(--space-3) 0 0;
    flex-wrap: wrap;
    gap: var(--space-3);
    width: max-content;
    max-width: 320px;
    padding: var(--space-6);
    border: var(--border-style-1);
    border-radius: var(--radius-3);
    background-color: var(--color-variant-danger-muted);

    &:popover-open {
      display: flex;
    }
  }
</style>
